import React from "react";
import {
    DashboardOutlined,
    HelpOutlineOutlined,
    HomeOutlined,
    TableChartOutlined,
} from "@mui/icons-material";

/**
 * THE RAIL, AND THE ONE PLACE IT IS DESCRIBED.
 *
 * THREE ENTRIES, because this app does one thing. It arrived here from t5, where the reporting
 * screen was a hidden guest -- one unlinked route among forty, reachable only by typing a URL, kept
 * out of the rail by a test. Here it IS the app, so it is in the rail and there is no test keeping
 * it out of one.
 *
 * NO PLACEHOLDERS. t5's rail lists what it does not have yet and marks it `placeholder`, which is
 * honest there because the shape of a data catalog is known and half-built. The shape of THIS app
 * is not agreed yet -- charts, saved reports and calculated fields are phases, not omissions -- and
 * a rail naming them would be a promise made by a nav bar. `placeholderItems` stays exported and
 * returns nothing, so the machinery is here when there is something to say.
 */

export type NavItem = {
    label: string;
    /** Where it goes. Every item has one, placeholder or not. */
    route: string;
    /** No screen behind it yet; AppRoutes renders the placeholder. */
    placeholder?: boolean;
    /** What the screen will hold, shown on the placeholder. Written for a reader, not a backlog. */
    note?: string;
    /** A NAME FOR THE ITEMS BELOW IT, not a destination -- so a group's children can arrive in
     *  sections. Carries no route and does not respond to a click. Only the Home group has any,
     *  and they come from the dashboards a reader can see rather than from this file. */
    heading?: boolean;
};

export type NavGroup = {
    icon: React.ReactNode;
    label: string;
    /** A group with no children is itself a destination. */
    route?: string;
    /** "search" opens the search dialog instead of navigating. "home" means the Sidebar fills this
     *  group's children in at run time from the dashboards this reader can see -- which is why
     *  they are not listed here: this file describes a rail that is the same for everybody, and
     *  those are not. */
    kind?: "search" | "home";
    children?: NavItem[];
    /** Rendered under the "Workspace" heading, as Ataccama's are. */
    workspace?: boolean;
    // About the app rather than a destination within it. The rail's single divider
    // sits above the first of these.
    trailing?: boolean;
};

const iconSx = {width: 22, height: 22};

export const NAV: NavGroup[] = [
    // HOME IS ALSO WHERE THE DASHBOARDS ARE, filled in by the Sidebar. A reader who has opened a
    // dashboard once should be able to get back to it from wherever they are, and before this the
    // only way was to go Home and use a dropdown that existed on that one page.
    {icon: <HomeOutlined sx={iconSx}/>, label: "Home", route: "/", kind: "home"},
    {icon: <TableChartOutlined sx={iconSx}/>, label: "Report builder", route: "/reporting",
     workspace: true},
    {icon: <DashboardOutlined sx={iconSx}/>, label: "Dashboards", route: "/dashboards",
     workspace: true},
    // ALWAYS IN THE RAIL, not only linked from the screen it describes. The Data Catalog's guides
    // were reachable only from their own pages, so somebody who had not already found a feature
    // could not find its guide either -- and nobody could see what documentation existed at all.
    // THE TRAILING GROUP. The rail's single divider sits above the first of these -- they are
    // about the app rather than destinations within it, which is the one distinction in the
    // rail worth a line.
    {icon: <HelpOutlineOutlined sx={iconSx}/>, label: "Documentation", route: "/documentation",
     trailing: true},
];

/** Administrators only, matching the server. See the note in Sidebar. */
export const ADMINISTRATOR_ONLY_ROUTES = new Set(["/approval-matrix"]);

/** Every placeholder in the rail, for AppRoutes to register. */
export function placeholderItems(): NavItem[] {
    const out: NavItem[] = [];
    for (const group of NAV) {
        for (const child of group.children ?? []) {
            if (child.placeholder) out.push(child);
        }
    }
    return out;
}
